import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { memberTabs } from 'src/modules/member/models/memberTabs';
import { MemberSearchFilter } from 'src/modules/member/models/memberSearchFilter';
import { getMembers } from 'src/shared/services/memberService';
import { HeaderSubTitle } from 'src/modules/dashboard/components/atoms/headerSubTitle';
import { WidgetInstance } from '../types';
import { useDashboardFilter } from '../dashboardFilterContext';

export type RealMembersByProfileConfig = {
  statusKeys: string[];
  profileItemIds: number[];
  profileItemLabels?: string;
};

const resolveStatusValues = (statusKeys: string[]): number[] => {
  if (!statusKeys.length) return [];
  return memberTabs.filter((t) => statusKeys.includes(t.key)).flatMap((t) => t.values);
};

type Props = {
  instance: WidgetInstance;
};

export const RealMembersByProfileWidget = ({ instance }: Props) => {
  const { t } = useTranslation(['member']);
  const config = (instance.config as RealMembersByProfileConfig | undefined) ?? { statusKeys: ['paid', 'notPaid'], profileItemIds: [] };
  const isMounted = useIsMounted();
  const { associationId, districtId } = useDashboardFilter();

  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [count, setCount] = useState<number>();

  const statusKey = config.statusKeys.join(',');
  const profileKey = config.profileItemIds.join(',');

  const statusLabel = (() => {
    if (!config.statusKeys.length) return 'Alla statusar';
    return memberTabs
      .filter((tab) => config.statusKeys.includes(tab.key))
      .map((tab) => t(tab.label))
      .join(' + ');
  })();

  const hasProfile = Boolean(config.profileItemLabels);
  const title = hasProfile ? (config.profileItemLabels as string) : 'Medlemmar per profil';
  const subtitle = hasProfile ? statusLabel : statusLabel;

  useEffect(() => {
    setLoading(true);
    setHasError(false);
    const filter: MemberSearchFilter = {
      status: resolveStatusValues(config.statusKeys),
      profileItemIds: config.profileItemIds,
      associationId,
      districtIds: districtId ? [districtId] : undefined,
    };
    getMembers(0, 1, 'memberNr', 'desc', filter, [], undefined)
      .then((res) => {
        if (isMounted()) setCount(res.data.totalItems);
      })
      .catch((e) => {
        ConsoleHelper.error(e);
        if (isMounted()) setHasError(true);
      })
      .finally(() => {
        if (isMounted()) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusKey, profileKey, isMounted, associationId, districtId]);

  return (
    <Stack sx={{ p: 2, height: '100%' }} spacing={1.25}>
      <Stack spacing={0.25}>
        <HeaderSubTitle label={title} />
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{subtitle}</Typography>
      </Stack>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        {(() => {
          if (loading) return <Skeleton variant='text' sx={{ fontSize: 36, width: '60%' }} />;
          if (hasError) return <Typography sx={{ color: 'error.main', fontSize: 13 }}>Kunde inte hämta data</Typography>;
          return (
            <Typography sx={{ fontSize: 38, fontWeight: 600, lineHeight: 1, letterSpacing: '-0.02em' }}>
              {count?.toLocaleString('sv-SE') ?? '–'}
              <Typography component='span' sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 400, ml: 0.75 }}>
                st
              </Typography>
            </Typography>
          );
        })()}
      </Box>
      {!config.profileItemIds.length && !loading && !hasError && (
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>Tips: klicka på kugghjulet i edit-läget för att välja profil</Typography>
      )}
    </Stack>
  );
};
